import { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Breadcrumb from '@/components/ui/Breadcrumb'
import Newsletter from '@/components/sections/Newsletter'
import Pagination from '@/components/ui/Pagination'
import { useAllWebPages } from '@/lib/api/hooks/web-pages'
import FullPageSpinner from '@/components/ui/FullPageSpinner'
import { useTenders, useSearchTenders } from '@/lib/api/hooks/tender'
import type { Tender, Procurement as ProcurementType } from '@/lib/api/types'
import { motion } from 'framer-motion'
import { fadeUp, staggerContainer, viewportReveal } from '@/lib/motion'
import { parseHtmlString } from '@/utils/helpers/html'
import { useProcurements, useIncrementProcurementDownload } from '@/lib/api/hooks/procurement'
import handleDownloadFromUrl from '@/utils/download'
import { toast } from 'sonner'
import Search from '@/components/ui/Search'
import FilterModule from '@/components/ui/FilterModule'
import FILTER_SECTIONS from '@/components/pages/Opportunities/Procurement/FilterModule/FILTER_SECTIONS'
import { useSearchFilterParams } from '@/hooks/useSearchFilterParams'
import DocumentIcon from '@/components/ui/DocumentIcon'
import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay'

interface TabType {
  id: string
  label: string
}

// Helper function to format bid type from API to display format
const formatBidType = (bidType: string): string => {
  const bidTypeMap: Record<string, string> = {
    'OPEN_BID': 'Open Tender',
    'RESTRICTED_BID': 'Restricted Tender',
    'REQUEST_FOR_PROPOSAL': 'Request for Proposal',
    'REQUEST_FOR_QUOTATION': 'Request for Quotation'
  }
  return bidTypeMap[bidType] || bidType
}

// Helper function to format date range
const formatDateRange = (startDate: string, endDate: string): string => {
  const start = new Date(startDate)
  const end = new Date(endDate)

  const formatDate = (date: Date): string => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December']
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
  }

  return `${formatDate(start)} to ${formatDate(end)}`
}

// Helper function to get tender status based on dates
const getTenderStatus = (startDate: string, endDate: string): string => {
  const now = new Date()
  const start = new Date(startDate)
  const end = new Date(endDate)

  if (now < start) {
    return 'Upcoming'
  } else if (now >= start && now <= end) {
    return 'Open'
  } else {
    return 'Closed'
  }
}

// Helper function to get year from date string
const getYearFromDate = (dateString: string): string => {
  return new Date(dateString).getFullYear().toString()
}



const Procurement = () => {
  const { data, isFetching, isError, refetch } = useAllWebPages();
  const procurementData = data?.data && data?.data.find((page) =>
    page.codeName?.toLowerCase() === 'procurement' ||
    page.websitePageName?.toLowerCase() === 'procurement'
  )?.content || null;

  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('')
  const [downloadingProcurements, setDownloadingProcurements] = useState<Set<string>>(new Set())

  // ── URL-driven search / filter state ──────────────────────────────────────
  const {
    mode,
    searchTerm,
    filterState,
    page,
    size,
    setSearch,
    setFilters,
    removeFilter,
    clearFilters,
    setPage,
    apiFilters,
    getActiveFilterCount
  } = useSearchFilterParams(FILTER_SECTIONS)


  // ── Search query (only active when mode === 'search') ─────────────────────
  const { data: searchData, isLoading: isSearchLoading, error: searchError } = useSearchTenders(
    searchTerm,
    { page, size },
  )

  // Filter API (when filtering or default)
  const { data: filterData, isLoading: isFilterLoading, error: filterError } = useTenders(
    { page, size },
    apiFilters
  )

  // ── Select the right data source based on mode ────────────────────────────
  const tendersData = mode === 'search' ? searchData : filterData
  const isLoading = mode === 'search' ? isSearchLoading : isFilterLoading
  const error = mode === 'search' ? searchError : filterError

  const processedTenders = Array.isArray(tendersData?.data?.data) ? tendersData.data.data.map((tender: Tender) => ({
    ...tender,
    status: getTenderStatus(tender.startDate, tender.endDate),
    year: getYearFromDate(tender.startDate)
  })) : []
  const totalPages = tendersData?.data?.totalPage ?? 1
  const activeFilterCount = getActiveFilterCount(FILTER_SECTIONS) 


  // Fetch procurements
  const { data: procurementsData, isLoading: procurementsLoading } = useProcurements()


  // Increment download mutation
  const incrementDownloadMutation = useIncrementProcurementDownload()


  // Group procurements by category
  const procurementDocuments = useMemo(() => {
    if (!procurementsData?.data) return []

    const procurements = procurementsData.data ?? [];
    const grouped: Record<string, ProcurementType[]> = {}

    procurements.forEach((procurement: ProcurementType) => {
      const category = procurement.documentCategory || 'Other'
      if (!grouped[category]) {
        grouped[category] = []
      }
      grouped[category].push(procurement)
    })

    // Convert to array format matching the original structure
    return Object.entries(grouped).map(([category, docs]) => ({
      category: category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      documents: docs.map((procurement: ProcurementType) => ({
        id: procurement.documentId,
        name: procurement.title,
        icon: <DocumentIcon url={procurement.documentUrl} />,
        url: procurement.documentUrl
      }))
    }))
  }, [procurementsData])



  // Handle document download with loading state
  const handleDocumentDownload = useCallback(async (documentId: string, url: string, title?: string) => {
    if (!url || url === '#') {
      toast.error('Document not available for download')
      return;
    }

    // Set loading state for this specific procurement
    setDownloadingProcurements(prev => {
      const newSet = new Set(prev)
      newSet.add(documentId)
      return newSet
    })

    try {
      // Increment download count
      await incrementDownloadMutation.mutateAsync(documentId)
      handleDownloadFromUrl({url, fileName:title})
    } catch (error) {
      console.error('Error downloading procurement:', error)
      // Fallback: open in new tab
      window.open(url, '_blank', 'noopener,noreferrer')
    } finally {
      // Remove loading state for this specific procurement
      setDownloadingProcurements(prev => {
        const newSet = new Set(prev)
        newSet.delete(documentId)
        return newSet
      })
    }
  }, [incrementDownloadMutation])

    const tabs: TabType[] = [
    { id: 'all', label: 'ALL TENDERS' },
    { id: 'latest', label: 'LATEST TENDERS' },
    { id: 'ending', label: 'ENDING SOON' }
  ]
    useEffect(() => {
    // get current tender tab from URL params on mount
    switch (true) {
      case filterState.endingSoon:
        setActiveTab('ending')
        break
      case filterState.latest:
        setActiveTab('latest')
        break
      default:
        setActiveTab('all')
    }
  }, [filterState])

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Procurement', href: '/opportunities', active: true }
  ]

  if (isFetching) {
    return <FullPageSpinner />;
  }

  if (isError) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Error loading page.</p>
      <button onClick={() => refetch()} className="px-4 py-2 bg-forestGreen-100 text-white rounded">Retry</button>
    </div>;
  }

  if (!procurementData) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Page content not found.</p>
    </div>;
  }

  return (
    <motion.div
      className="bg-white min-h-screen"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      {/* Breadcrumb */}
      <motion.div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" variants={fadeUp}>
        <Breadcrumb items={breadcrumbItems} />
      </motion.div>

      {/* Main Content */}
      <motion.div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-16" variants={fadeUp}>
        {/* Hero Title */}
        <div className="mb-5">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-normal font-pt-serif text-gray-900 leading-tight">
            {procurementData?.PROCUREMENT?.title ?? "Afsa Procurement"}
          </h1>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-14">
          {/* Left Content */}
          <div className="lg:col-span-2 order-2 lg:order-1 space-y-16">
            {/* Hero Image */}
            <div className="space-y-8">
              <img
                src={procurementData?.PROCUREMENT?.fileUrl ?? "https://res.cloudinary.com/dlwegkhme/image/upload/v1756906407/9ea2726bb80a9400295627ef524d12bb98744652_cuypz2.png"}
                alt="AFSA facilities and infrastructure for space technology development"
                className="w-full h-64 sm:h-80 md:h-96 lg:h-[480px] object-cover"
              />

              {/* Introduction Text */}
              <div className="space-y-6">
                <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed">
                  <span dangerouslySetInnerHTML={parseHtmlString(procurementData?.PROCUREMENT?.description ?? "")} />
                </p>


              </div>
            </div>

            {/* Procurement Opportunities Section */}
            <div className="space-y-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal font-pt-serif text-gray-900 leading-tight">
                Procurement Opportunities
              </h2>
                 {/* Search + Filter Controls */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center relative gap-4 justify-between flex-wrap">
              <Search
                value={searchTerm}
                onSearch={setSearch}
                placeholder="Search events..."
              />
               <FilterModule
                    sections={FILTER_SECTIONS}
                    activeFilters={filterState}
                    onApply={setFilters}
                    activeFilterCount={activeFilterCount}
                  />
            </div>

            {/* Active filter chips — only shown in filter mode */}
            <FilterParamsDisplay
              sections={FILTER_SECTIONS}
              filterState={filterState}
              onRemove={removeFilter}
              onClearAll={clearFilters}
            />
          </div>

              <div className="space-y-8">
                {/* Tabs */}
                <div className="flex">
                  {tabs.map((tab, index) => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        const updatedFilters = { ...filterState }
                        Object.keys(updatedFilters).forEach(key => delete updatedFilters[key])
                        if (tab.id === 'latest') {
                          updatedFilters.latest = 'true'
                        } else if (tab.id === 'ending') {
                          updatedFilters.endingSoon = 'true'
                        }
                        setFilters(updatedFilters)
                       
                      }}
                      className={`flex-1 p-4 text-center transition-colors ${tab.id === activeTab
                        ? 'bg-wine-100 text-white font-bold'
                        : 'bg-white text-gray-900 font-normal hover:bg-gray-50'
                        } ${index === 0 ? 'rounded-tl' : ''} ${index === tabs.length - 1 ? 'rounded-tr' : ''}`}
                    >
                      <span className="text-base font-satoshi">
                        {tab.label}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Tender Cards Grid */}
                <div className="space-y-10">
                  {isLoading ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className="px-4 py-6 bg-gray-100 animate-pulse rounded-md h-48" />
                      ))}
                    </div>
                  ) : error ? (
                    <div className="text-center py-12">
                      <p className="text-red-600 mb-4">Error loading tenders. Please try again later.</p>
                    </div>
                  ) : processedTenders.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-600 text-lg">
                        {searchTerm
                          ? 'No tenders found matching your search.'
                          : 'No tenders found matching the selected filters.'}
                      </p>
                    </div>
                  ) : (
                    <>
                      <motion.div
                        className="grid grid-cols-1 lg:grid-cols-2 gap-5"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={viewportReveal}
                      >
                        {processedTenders.map((tender: Tender) => (
                          <motion.div
                            key={tender.tenderId}
                            onClick={() => navigate(`/opportunities/${tender.tenderId}`)}
                            className="px-4 py-6 bg-gray-50 rounded-md hover:bg-wine-200 hover:text-white transition-all duration-200 cursor-pointer group"
                            variants={fadeUp}
                            whileHover={{ y: -4 }}
                            transition={{ type: 'spring', stiffness: 280, damping: 24 }}
                          >
                            <div className="space-y-8">
                              <div className="space-y-4">
                                <h3 className="text-lg font-normal font-pt-serif text-gray-900 group-hover:text-white leading-relaxed">
                                  {tender.tenderTitle}
                                </h3>
                                <div className="px-4 py-2 bg-white rounded inline-block">
                                  <span className="text-sm font-normal text-wine-200">
                                    {formatBidType(tender.bidType)}
                                  </span>
                                </div>
                              </div>
                              <div className="text-base font-normal font-satoshi text-gray-900 group-hover:text-white">
                                {formatDateRange(tender.startDate, tender.endDate)}
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </motion.div>

                     
                        <Pagination
                          currentPage={page}
                          totalPages={totalPages}
                          onPageChange={setPage}
                        />
                     
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="lg:col-span-1 order-1 lg:order-2">
            <div className="space-y-10">
              {/* Procurement Documents Sections */}
              <div className="space-y-10">
                {procurementsLoading ? (
                  <div className="space-y-6">
                    <div className="h-8 bg-gray-200 animate-pulse rounded" />
                    <div className="border border-gray-300 rounded-md overflow-hidden">
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="pl-3 pr-4 py-2.5 bg-surfaceColor-100 flex items-center gap-1.5 border-gray-300 border-b last:border-b-0">
                          <div className="w-8 h-8 bg-gray-200 animate-pulse rounded" />
                          <div className="h-5 bg-gray-200 animate-pulse rounded flex-1" />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : procurementDocuments.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500">No procurements available.</p>
                  </div>
                ) : (
                  procurementDocuments.map((section) => (
                    <div key={section.category} className="space-y-6">
                      <h3 className="text-2xl sm:text-3xl font-normal font-pt-serif text-forestGreen-100">
                        {section.category}
                      </h3>

                      <div className="border border-gray-300 rounded-md overflow-hidden">
                        {section.documents.map((doc, index) => {
                          // Ensure we use the documentId directly for unique identification
                          const documentId = doc.id
                          const isDownloading = downloadingProcurements.has(documentId)

                          return (
                            <div
                              key={`${section.category}-${documentId}`}
                              onClick={(e) => {
                                e.stopPropagation()
                                if (!isDownloading) {
                                  handleDocumentDownload(documentId, doc.url, doc.name)
                                }
                              }}
                              className={`pl-3 pr-4 py-2.5 bg-surfaceColor-100 grid grid-cols-[2rem_1fr] items-center gap-1.5 hover:bg-gray-200 transition-colors ${isDownloading ? 'opacity-50 cursor-wait' : 'cursor-pointer'
                                } ${index === 0 ? 'border-gray-300 outline outline-1 outline-gray-300' : 'border-l border-r border-b border-gray-300'
                                } ${index === 0 ? 'rounded-t-md' : ''} ${index === section.documents.length - 1 ? 'rounded-b-md' : ''}`}
                            >            
                            {
                              doc.icon
                            }                  
                                
                              
                              <span className="text-base font-normal font-satoshi text-gray-900 leading-normal flex-1">
                                {doc.name}
                              </span>
                              {isDownloading && (
                                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>

            

              {/* Newsletter */}
              <Newsletter />
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default Procurement